import axios from 'axios';
import * as _ from 'lodash';
import { store } from './reducers';
import {baseApiURL} from "./Utils/urls";
import {encryptData, decryptData} from "./Helper/EncryptionDecryption";
import {installUrlUpdateDiagnostics} from "./Utils/diagnoseUrlUpdate";

const api = axios.create({
  baseURL: baseApiURL,
});

// Diagnostic interceptor — inert unless localStorage.diagnoseUrlUpdate === '1'.
// MUST be installed before the encryption interceptor below so it sees the
// pre-encryption payload (axios runs request interceptors in REVERSE order).
installUrlUpdateDiagnostics(api);

// Decode the `_id` claim out of a JWT without pulling in a new dependency.
// Returns null on any failure (missing token, malformed parts, bad base64, non-JSON
// body, missing _id). Intentionally silent — never log token data to console.
const decodeJwtUserId = (token: any): string | null => {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    // Normalize base64url → base64 so `atob` accepts it.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    const id = payload && (payload._id || payload.id);
    return id ? String(id) : null;
  } catch (_e) {
    return null;
  }
};

api.interceptors.request.use((config) => {
  const state = store.getState();
const uniquetokennew = state.adminUser.adminUser.apiToken;
  const language = _.get(state, 'adminUser.adminUser.language', null);
  const configCopy = { ...config };
  if(configCopy.data && configCopy.data !== undefined && !(configCopy.data instanceof FormData)){
    // Resolve userAuthId resiliently:
    //   1) Redux state (current happy path — preserves working users)
    //   2) JWT `_id` decoded from the apiToken Redux already has
    //      (covers the asymmetric-rehydrate case where apiToken survived but userId didn't)
    //   3) Leave undefined — backend will reject as before; do not mask
    const reduxUserId = state.adminUser.adminUser.userId;
    const jwtUserId = reduxUserId ? null : decodeJwtUserId(uniquetokennew);
    configCopy.data.userAuthId = reduxUserId || jwtUserId;
    configCopy.data.encryptiondta = true;
    configCopy.data = {token: encryptData(configCopy.data)};
  }
  if (uniquetokennew) {
    configCopy.headers['Authorization'] = `${uniquetokennew}`// Corrected the format of the Authorization header
  }
  if (language) {
    configCopy.headers['language'] = `${language}`;
  } else {
    configCopy.headers['language'] = 'en';
  }

  if (!configCopy.headers['X-Selected-Country']) {
    const selectedCountry = _.get(state, 'adminUser.adminUser.selectedCountry', 'ALL');
    configCopy.headers['X-Selected-Country'] = selectedCountry;
  }

  return configCopy;
});

// ================================
// api.interceptors.request.use(config => {
//   const state = store.getState();
//   const uniquetokennew = state.adminUser.adminUser.apiToken; // Corrected the path to the token
//   console.log(state, uniquetokennew, "uniquetokennew");
//   if (uniquetokennew) {
//     console.log(uniquetokennew, "uniquetokennew 15");
//     config.headers.Authorization = `${uniquetokennew}`; // Corrected the format of the Authorization header
//   }
//   return config;
// }, error => {
//   return Promise.reject(error);
// });
// =================================

// Double-submit prevention: block identical concurrent write requests only
// Read endpoints (getAll, get-user, etc.) are excluded — they're safe to call in parallel
const pendingRequests = new Map<string, boolean>();
const WRITE_PATTERNS = ['/create', '/add', '/update', '/delete', '/redeem', '/bulkUpload'];

api.interceptors.request.use((config: any) => {
  if (config.method === 'post' || config.method === 'put') {
    const url = config.url?.toLowerCase() || '';
    const isWrite = WRITE_PATTERNS.some(p => url.includes(p));
    if (isWrite) {
      const key = `${config.method}:${config.url}:${JSON.stringify(config.data)}`;
      if (pendingRequests.has(key)) {
        const cancelSource = axios.CancelToken.source();
        config.cancelToken = cancelSource.token;
        cancelSource.cancel('Duplicate request blocked');
        return config;
      }
      pendingRequests.set(key, true);
      config._dedupKey = key;
    }
  }
  return config;
});

api.interceptors.response.use((res: any) => {
  if (res.config?._dedupKey) {
    pendingRequests.delete(res.config._dedupKey);
  }
  // Skip decryption for binary blob responses (e.g. Excel file downloads)
  if (res.config?.responseType === 'blob') {
    return res;
  }
  try {
    res.data = decryptData(res.data);
  } catch (_e) {
    // Response was not encrypted (e.g. file upload endpoints) — use as-is
  }
  return res;
}, async (error) => {
  if (error.config?._dedupKey) {
    pendingRequests.delete(error.config._dedupKey);
  }
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }
  let errorResponse = _.get(error, 'response.data', null);
  if (errorResponse) {
    // Blob-typed requests (file/export downloads) deliver the error body as a
    // Blob even on non-2xx — the backend still sends a JSON error object.
    // Read it as text first so it can be parsed instead of fed to decryptData
    // (which expects a base64 string and throws "Array of Buffers" on a Blob).
    if (typeof Blob !== 'undefined' && errorResponse instanceof Blob) {
      try {
        errorResponse = await errorResponse.text();
      } catch (_e) {
        throw new Error(error.message);
      }
    }
    // The error body may be (a) an encrypted base64 string, (b) a plain JSON
    // error object/string, or (c) a JSON string. Try each in order.
    let errorResponse1: any;
    try {
      errorResponse1 = decryptData(errorResponse);
    } catch (_e) {
      if (typeof errorResponse === 'string') {
        try { errorResponse1 = JSON.parse(errorResponse); }
        catch (_e2) { errorResponse1 = { message: errorResponse }; }
      } else {
        errorResponse1 = errorResponse;
      }
    }
    if(errorResponse1.message === "You are not authorized" || errorResponse1.message === "User not found"){
      console.log('[AUTH-REDIRECT]', {
        status: error && error.response && error.response.status,
        url: error && error.config && error.config.url,
        data: error && error.response && error.response.data,
      });
      localStorage.clear();
      window.location.href = "/";
    }
    // Ensure we are throwing a string message, not an object
    const errorMessage = typeof errorResponse1.message === 'object'
      ? JSON.stringify(errorResponse1.message)
      : (errorResponse1.message || "Unknown Error");

    throw new Error(errorMessage);
  }
  throw new Error(error.message);
});

export { api };
